import { useState, useEffect } from 'react';
import { API_URL } from "../../shared/constant";
import { Oval } from "react-loader-spinner";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import Swal from "sweetalert2";
import { isPWA, isMobilePWA } from "../../utils/pwa";
import { isWebView } from "../../utils/webview";
import "../../styles/pwa-customers.css";

const OrderPermission = () => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState([]);
    const [formData, setFormData] = useState({
        user_id: "",
    });
    const [searchValue, setSearchValue] = useState("");
    const [pwaMode, setPwaMode] = useState(false);
    const [mobilePWA, setMobilePWA] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [refreshing, setRefreshing] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [webViewMode, setWebViewMode] = useState(false);

    const fetchOptions = async (inputValue) => {
        if (inputValue && inputValue.length > 2) {
            try {
                const res = await axios.post(API_URL.USERS_SEARCH, { text: inputValue });
                return res.data.map((user) => ({
                    label: user.name,
                    value: user.id,
                }));
            } catch (error) {
                console.error('API fetch error:', error);
                return [];
            }
        } else {
            return [];
        }
    };

    const getUser = () => {
        setLoading(true);
        axios.post(API_URL.USERS_DIRECT, {}).then(
            (res) => {
                if (res.data.status === 1) {
                    setUserData(res.data.data);
                }
                setLoading(false);
                setRefreshing(false);
            },
            (error) => {
                setLoading(false);
                setRefreshing(false);
                toast.error("Something went wrong..", {
                    autoClose: 3000,
                });
            }
        );
    };


    const adduser = () => {
            setLoading(true);
            axios.post(API_URL.USERS_ADD_DIRECT, {
                user_id: formData.user_id,
            })
                .then((res) => {
                    if (res.data.status === 1) {
                        let message = "Permission added";
                        toast.success(message, { autoClose: 3000, position: "top-right" });
                        setLoading(false);
                        getUser();
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
    };

    const deleteParty = (row) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to remove permission for user " + row.name,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, remove it!",
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                axios.post(API_URL.USERS_REMOVE_DIRECT, { user_id: row.user_id }).then(
                    (res) => {
                        if (res.data.status === 1) {
                            let newData = userData.filter((r) => r.user_id != row.user_id);
                            setUserData(newData);
                        }
                        toast.success("Permission removed successfully", {
                            autoClose: 3000
                        });
                        setLoading(false);
                    },
                    (error) => {
                        setLoading(false);
                        toast.error("Something went wrong..", {
                            autoClose: 3000,
                            
                        });
                    }
                );
            }
        });
    };

    const handleChange = (option) => {
        setSelectedOption(option);
        const selectedId = option ? option.value : "";
        setFormData({ user_id: selectedId });
    };

    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            if (pwaMode) {
                toast.success("Connection restored", { autoClose: 2000 });
                getUser(); // Refresh data when back online
            }
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            if (pwaMode) {
                toast.warning("You're offline. Some features may not work.", { autoClose: 3000 });
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [pwaMode]);

    // Pull to refresh functionality
    useEffect(() => {
        if (!pwaMode || !mobilePWA) return;

        let startY = 0;
        let currentY = 0;
        let isPulling = false;
        let pullElement = null;

        const handleTouchStart = (e) => {
            if (window.scrollY === 0) {
                startY = e.touches[0].clientY;
                pullElement = document.querySelector('.pwa-orderpermission-list');
            }
        };

        const handleTouchMove = (e) => {
            if (window.scrollY === 0 && pullElement) {
                currentY = e.touches[0].clientY;
                const pullDistance = currentY - startY;

                if (pullDistance > 50) {
                    isPulling = true;
                    pullElement.style.transform = `translateY(${Math.min(pullDistance * 0.3, 50)}px)`;
                    pullElement.style.opacity = '0.7';
                }
            }
        };

        const handleTouchEnd = () => {
            if (isPulling && pullElement) {
                pullElement.style.transform = '';
                pullElement.style.opacity = '';
                
                setRefreshing(true);
                getUser().finally(() => {
                    setRefreshing(false);
                    toast.success("Data refreshed", { autoClose: 1500 });
                });
                
                isPulling = false;
            }
        };

        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [pwaMode, mobilePWA]);

    useEffect(() => {
        getUser();
        // Check PWA mode, mobile, and webview
        setPwaMode(isPWA());
        setMobilePWA(isMobilePWA());
        setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        setWebViewMode(isWebView());
    }, []);

    return (
        <div>
            {/* PWA Header - Only show in PWA mode or mobile (not webview) */}
            {(pwaMode || (isMobile && !webViewMode)) && (
                <div className="pwa-header bg-primary text-white p-3 mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <h4 className="mb-0">
                            <i className="simple-icon-shield me-2"></i>
                            Order Permission ({userData.length})
                        </h4>
                        <div className="d-flex align-items-center gap-2">
                            {!isOnline && (
                                <div className="pwa-status-indicator bg-warning">
                                    <i className="simple-icon-wifi me-1"></i>
                                    Offline
                                </div>
                            )}
                            {mobilePWA && (
                                <div className="pwa-status-indicator">
                                    <i className="simple-icon-screen-smartphone me-1"></i>
                                    PWA
                                </div>
                            )}
                            {refreshing && (
                                <div className="pwa-status-indicator bg-info">
                                    <i className="simple-icon-refresh me-1"></i>
                                    Refreshing
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <div className={`row ${(pwaMode || (isMobile && !webViewMode)) ? 'pwa-content' : ''}`}>
                {!(pwaMode || (isMobile && !webViewMode)) && (
                    <div className="col-6">
                        <h1>Order Permission</h1>
                        {webViewMode && (
                            <small className="text-muted">
                                <i className="simple-icon-screen-smartphone me-1"></i>
                                WebView Mode
                            </small>
                        )}
                    </div>
                )}
                
                <div className="col-md-12">
                    {(pwaMode || (isMobile && !webViewMode)) ? (
                        // PWA Mobile Card Layout
                        <div className="pwa-orderpermission-list">
                            {pwaMode && mobilePWA && (
                                <div className="pwa-pull-hint text-center text-muted mb-3">
                                    <small>
                                        <i className="simple-icon-arrow-down me-1"></i>
                                        Pull down to refresh
                                    </small>
                                </div>
                            )}
                            
                            {/* PWA Add User Section */}
                            <div className="pwa-add-section mb-3">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Order Settings</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <label className="form-label">Who can send you direct order</label>
                                            <AsyncSelect
                                                cacheOptions
                                                loadOptions={fetchOptions}
                                                defaultOptions
                                                onChange={handleChange}
                                                value={selectedOption}
                                                placeholder="Search User...."
                                                isClearable
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        borderColor: '#ccc',
                                                        boxShadow: 'none',
                                                        '&:hover': { borderColor: '#aaa' },
                                                    }),
                                                }}
                                            />
                                        </div>
                                        <button
                                            onClick={() => adduser()}
                                            className="btn btn-primary w-100"
                                        >
                                            <i className="simple-icon-plus me-1"></i> Add Permission
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* PWA Search Section */}
                            <div className="pwa-search-section mb-3">
                                <div className="input-group">
                                    <input
                                        type="text"
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        className="form-control form-control-lg"
                                        placeholder="Search by name, contact, city..."
                                    />
                                    <button className="btn btn-outline-secondary" type="button">
                                        <i className="simple-icon-magnifier"></i>
                                    </button>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-4">
                                    <Oval
                                        height={60}
                                        width={60}
                                        color="#7539ff"
                                        wrapperStyle={{ justifyContent: "center" }}
                                        wrapperClass=""
                                        visible={true}
                                        ariaLabel="oval-loading"
                                        secondaryColor="#7539ff"
                                        strokeWidth={2}
                                        strokeWidthSecondary={2}
                                    />
                                </div>
                            ) : (
                                <div>
                                    {userData.filter(user => 
                                        !searchValue || 
                                        user.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                                        user.contact_no?.toLowerCase().includes(searchValue.toLowerCase()) ||
                                        user.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
                                        user.city_name?.toLowerCase().includes(searchValue.toLowerCase())
                                    ).map((row, key) => {
                                        return (
                                            <div key={key} className="pwa-orderpermission-card mb-3">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <h6 className="card-title mb-1">{row.name}</h6>
                                                                <small className="text-muted">#{key + 1}</small>
                                                            </div>
                                                            <div className="pwa-status-badge">
                                                                <span className="badge bg-success">Allowed</span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="pwa-orderpermission-details mb-3">
                                                            <div className="row">
                                                                <div className="col-6">
                                                                    <small className="text-muted d-block">Contact</small>
                                                                    <small>{row.contact_no}</small>
                                                                </div>
                                                                <div className="col-6">
                                                                    <small className="text-muted d-block">City</small>
                                                                    <small>{row.city_name}, {row.state_name}</small>
                                                                </div>
                                                            </div>
                                                            <div className="row mt-2">
                                                                <div className="col-12">
                                                                    <small className="text-muted d-block">Address</small>
                                                                    <small>{row.address}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="pwa-actions">
                                                            <button
                                                                onClick={() => deleteParty(row)}
                                                                className="btn btn-outline-danger btn-sm w-100"
                                                            >
                                                                <i className="simple-icon-trash me-1"></i> Remove Permission
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {userData.filter(user => 
                                        !searchValue || 
                                        user.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                                        user.contact_no?.toLowerCase().includes(searchValue.toLowerCase()) ||
                                        user.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
                                        user.city_name?.toLowerCase().includes(searchValue.toLowerCase())
                                    ).length === 0 && (
                                        <div className="text-center py-5">
                                            <i className="simple-icon-shield" style={{fontSize: '3rem'}}></i>
                                            <h5>No permissions found</h5>
                                            <p className="text-muted">Add users who can send you direct orders</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        // Original Desktop Layout
                        <>
                            <div className="card">
                                <div className="card-header">Order Settings</div>
                                <div className="card-body">
                                    <div className="d-flex justify-content-center align-items-center">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Who can send you direct order</label>
                                                <AsyncSelect
                                                    cacheOptions
                                                    loadOptions={fetchOptions}
                                                    defaultOptions
                                                    onChange={handleChange}
                                                    value={selectedOption}
                                                    placeholder="Search User...."
                                                    isClearable
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            borderColor: '#ccc',
                                                            boxShadow: 'none',
                                                            '&:hover': { borderColor: '#aaa' },
                                                        }),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className='col-md-6'>
                                            <button
                                                onClick={() => adduser()}
                                                className="btn btn-primary"
                                            >
                                                <i className="simple-icon-plus me-1"></i> Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-12 pt-3">
                                <div className="card">
                                    <div className="card-body p-0">
                                        {loading ? (
                                            <Oval
                                                height={60}
                                                width={60}
                                                color="#7539ff"
                                                wrapperStyle={{ justifyContent: "center" }}
                                                wrapperClass=""
                                                visible={true}
                                                ariaLabel="oval-loading"
                                                secondaryColor="#7539ff"
                                                strokeWidth={2}
                                                strokeWidthSecondary={2}
                                            />
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table">
                                                    <thead className="">
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Contact No</th>
                                                            <th>Address</th>
                                                            <th>City</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {userData.map((row, key) => {
                                                            return (
                                                                <tr key={key}>
                                                                    <td>
                                                                        {row.name}
                                                                    </td>
                                                                    <td>{row.contact_no}</td>
                                                                    <td>{row.address}</td>
                                                                    <td>{row.city_name}, {row.state_name}</td>
                                                                    <td>
                                                                        <button
                                                                            onClick={() => deleteParty(row)}
                                                                            className="btn btn-sm btn-danger me-2"
                                                                        >
                                                                            <i className="simple-icon-trash me-1"></i> Delete
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderPermission;
